/**
 * Media library. Every stored object is guaranteed to be at or under the size
 * limit — the API refuses to write anything larger to R2.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader } from '../App'
import { api, useApi, qs } from '../lib/api'
import { PartnerSelect, EnumSelect } from '../components/Select'
import { ImageUpload, MediaImage, AiAltTextButton } from '../components/ImageUpload'
import {
  Card, Tile, Field, Badge, Empty, Pager, Loading, ErrorState, Modal, Notice, Filters, FilterActions,
  InfoButton, countFilters, SearchInput, ConfirmModal, QueryState, fmtInt, fmtBytes, fmtDateTime, fmtDate,
  useToast, Spinner,
} from '../components/ui'
import { useCode } from '../lib/terms'
import { Icon } from '../components/Icon'

/**
 * What AI has cost this archive on the image side.
 *
 * Only captioning spends model time: resizing runs through the Cloudflare Images
 * binding (and a canvas pass in the browser), which is a pixel transform, not
 * inference. Neither Workers AI nor the external provider APIs hand this app a
 * per-call credit or token figure, so what is recorded per run is characters and
 * duration — the token column is an estimate derived from those.
 */
function AiCreditsPanel() {
  const query = useApi('stats-image-ai', 'stats/image-ai')
  const [open, setOpen] = useState(false)

  return (
    <QueryState query={query}>
      {(d) => {
        const t = d.totals
        // ~4 characters per token is the usual English rule of thumb; it is an
        // estimate and is labelled as one wherever it is shown.
        const estTokens = Math.round((t.input_chars + t.output_chars) / 4)
        return (
          <Card
            title="AI Image Credits"
            sub="Historic model usage on the image pipeline — alt text and captions"
            actions={
              <>
                <InfoButton title="How to read these numbers">
                  <p>
                    Resizing costs no AI credits — it is a Cloudflare Images transform plus a
                    browser-side canvas pass, with no model involved. The only model work here is
                    the vision model that writes alt text, once per upload where you leave the field
                    empty.
                  </p>
                  <p style={{ marginTop: 10 }}>
                    The providers do not return a cost per call to this app, so the figures shown
                    are the runs themselves plus a character-based token estimate, not a billed
                    amount. Check your Cloudflare or provider dashboard for actual spend.
                  </p>
                </InfoButton>
                <Link className="btn btn-sm" to="/reports/ai-usage">
                  Full AI Usage
                </Link>
                <button className="btn btn-sm" onClick={() => setOpen((v) => !v)}>
                  {open ? 'Hide detail' : 'Show detail'}
                </button>
              </>
            }
          >
            <div className="grid grid-4">
              <Tile
                label="Captioning runs"
                value={fmtInt(t.runs)}
                note={t.errors ? `${fmtInt(t.errors)} failed` : 'no failures'}
              />
              <Tile
                label="Images captioned by AI"
                value={fmtInt(d.library.ai_captioned)}
                note={`of ${fmtInt(d.library.images)} stored`}
              />
              <Tile label="Estimated tokens" value={fmtInt(estTokens)} note="≈ characters ÷ 4" />
              <Tile
                label="Inference time"
                value={t.total_ms >= 60000 ? `${(t.total_ms / 60000).toFixed(1)} min` : `${fmtInt(t.total_ms)} ms`}
                note={t.last_run ? `last run ${fmtDateTime(t.last_run)}` : 'never run'}
              />
            </div>

            {open && (
              <div className="col" style={{ marginTop: 4 }}>
                {!t.runs ? (
                  <Empty icon="ai" title="No image AI runs recorded yet">
                    Upload an image and leave the alt text empty — the vision model writes it and
                    the run is recorded here.
                  </Empty>
                ) : (
                  <>
                    <div className="table-wrap flush">
                      <table className="data compact">
                        <thead>
                          <tr>
                            <th>Model</th>
                            <th>Provider</th>
                            <th className="right">Runs</th>
                            <th className="right">Failures</th>
                            <th className="right">Avg latency</th>
                            <th className="right">Est. tokens</th>
                          </tr>
                        </thead>
                        <tbody>
                          {d.byModel.map((r, i) => (
                            <tr key={i}>
                              <td className="tiny mono">{r.model_key}</td>
                              <td className="small">{r.provider_key}</td>
                              <td className="num">{fmtInt(r.n)}</td>
                              <td className="num">
                                {r.errors ? <Badge tone="danger">{r.errors}</Badge> : <span className="subtle">0</span>}
                              </td>
                              <td className="num">{fmtInt(r.avg_ms)} ms</td>
                              <td className="num">{fmtInt(Math.round(r.output_chars / 4))}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="table-wrap flush">
                      <table className="data compact">
                        <thead>
                          <tr>
                            <th>Month</th>
                            <th className="right">Runs</th>
                            <th className="right">Failures</th>
                            <th className="right">Est. tokens</th>
                          </tr>
                        </thead>
                        <tbody>
                          {d.byMonth.map((r) => (
                            <tr key={r.month}>
                              <td className="mono small">{r.month}</td>
                              <td className="num">{fmtInt(r.n)}</td>
                              <td className="num">{r.errors ? fmtInt(r.errors) : <span className="subtle">0</span>}</td>
                              <td className="num">{fmtInt(Math.round(r.output_chars / 4))}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="small subtle">
                      First recorded run {fmtDateTime(t.first_run)}. Every run, successful or not,
                      is listed in full on the AI Usage report.
                    </p>
                  </>
                )}
              </div>
            )}
          </Card>
        )
      }}
    </QueryState>
  )
}

/** What to call an image in a button label or a confirmation — filename first, since
 *  that is what the person who uploaded it recognises. */
const imageName = (r) => r?.filename || r?.alt_text || `image #${r?.id}`

/** No filters, named once so the panel's ✕ and its Clear button mean the same thing. */
const EMPTY = { q: '', kind: null, partner_id: null }

/** Pictures per page; the first is the default. */
const PAGE_SIZES = [50, 100, 250]

export default function MediaLibrary({ user }) {
  const code = useCode()
  const toast = useToast()
  const [f, setF] = useState(EMPTY)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(PAGE_SIZES[0])
  const [uploadOpen, setUploadOpen] = useState(false)
  const [selected, setSelected] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [busy, setBusy] = useState(false)
  const [alt, setAlt] = useState('')

  const set = (patch) => {
    setF((prev) => ({ ...prev, ...patch }))
    setPage(1)
  }

  const query = useApi(['media', f, page, limit], `media${qs({ ...f, page, limit })}`, {
    keepPreviousData: true,
  })

  const saveAlt = async () => {
    setBusy(true)
    try {
      await api(`media/${selected.id}`, { method: 'PUT', body: { alt_text: alt } })
      toast.ok('Alt text saved')
      setSelected(null)
      query.refetch()
    } catch (err) {
      toast.error(err)
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    setBusy(true)
    try {
      await api(`media/${confirmDelete.id}`, { method: 'DELETE' })
      toast.ok(`Deleted ${imageName(confirmDelete)}`)
      setConfirmDelete(null)
      setSelected(null)
      // The last image on a later page would leave the grid empty with a pager
      // pointing past the end, so step back a page instead.
      if ((query.data?.rows?.length ?? 0) === 1 && page > 1) setPage(page - 1)
      else query.refetch()
    } catch (err) {
      toast.error(err)
    } finally {
      setBusy(false)
    }
  }

  const rows = query.data?.rows ?? []
  const totalBytes = rows.reduce((a, r) => a + r.bytes, 0)
  const savedBytes = rows.reduce((a, r) => a + Math.max(0, (r.original_bytes ?? r.bytes) - r.bytes), 0)

  return (
    <PageHeader
      title="Media Library"
      sub={query.data ? `${fmtInt(query.data.total)} images in R2` : 'Loading…'}
      actions={
        user.caps.uploadMedia ? (
          <button className="btn btn-sm btn-primary" onClick={() => setUploadOpen(true)}>
            + Upload Images
          </button>
        ) : null
      }
    >
      <div className="col">
        <Notice icon="compress" title="Every image is resized before storage">
          Uploads are shrunk in the browser and re-checked by the Worker. Nothing above{' '}
          {Math.round(user.maxImageBytes / 1024)} KB is written to R2 — an image that cannot be
          brought under the limit is rejected rather than stored.
        </Notice>

        <div className="grid grid-4">
          <Tile label="Images on this page" value={fmtInt(rows.length)} />
          <Tile label="Stored size" value={fmtBytes(totalBytes)} />
          <Tile label="Saved by resizing" value={fmtBytes(savedBytes)} tone="ok" />
          <Tile
            label="Largest"
            value={fmtBytes(rows.reduce((a, r) => Math.max(a, r.bytes), 0))}
            note={`limit ${Math.round(user.maxImageBytes / 1024)} KB`}
          />
        </div>

        <AiCreditsPanel />

        {/* Above the panel, as on every list. */}
        <SearchInput
          id="m-q"
          value={f.q}
          onChange={(v) => set({ q: v })}
          placeholder="Filename or alt text…"
          aria-label="Search the media library"
        />
        <Filters id="media" active={countFilters(f)} onClear={() => set(EMPTY)}>
          <Field label="Kind" htmlFor="m-kind">
            <EnumSelect
              inputId="m-kind"
              value={f.kind}
              onChange={(v) => set({ kind: v })}
              options={[
                { value: 'partner_photo', label: 'Partner unit photo' },
                { value: 'newsletter_image', label: 'Update image' },
                { value: 'other', label: 'Other' },
              ]}
            />
          </Field>
          <Field label="Partner Unit" htmlFor="m-partner">
            <PartnerSelect inputId="m-partner" value={f.partner_id} onChange={(v) => set({ partner_id: v })} />
          </Field>
          <FilterActions active={countFilters(f)} onClear={() => set(EMPTY)} busy={query.isFetching} />
        </Filters>

        {query.isLoading && (
          <Card>
            <Loading />
          </Card>
        )}
        {query.isError && (
          <Card>
            <ErrorState error={query.error} retry={query.refetch} />
          </Card>
        )}

        {query.data && !rows.length && (
          <Card>
            <Empty icon="image" title="No images yet">
              {user.caps.uploadMedia
                ? 'Upload a partner unit photo to see it here and on the report page.'
                : 'Nothing has been uploaded to this archive yet.'}
            </Empty>
          </Card>
        )}

        {rows.length ? (
          <Card>
            <div className="media-grid">
              {rows.map((r) => (
                <div className="media-item" key={r.id}>
                  <button
                    className="media-item-open"
                    onClick={() => {
                      setSelected(r)
                      setAlt(r.alt_text ?? '')
                    }}
                  >
                    <MediaImage id={r.id} alt={r.alt_text} />
                    <div className="media-meta">
                      <div className="truncate">{r.alt_text || r.filename || 'untitled'}</div>
                      <div className="row row-tight tiny subtle" style={{ marginTop: 3 }}>
                        <span>{fmtBytes(r.bytes)}</span>
                        {r.original_bytes > r.bytes && <Badge tone="ok">resized</Badge>}
                        {r.ai_caption && <Badge tone="accent" title="Alt text written by AI">AI</Badge>}
                      </div>
                    </div>
                  </button>
                  {/*
                    Deleting used to mean opening the image first and finding the button
                    in the dialog footer. It is on the tile itself now — a sibling of the
                    tile button, never nested inside it — and still asks before it acts.
                  */}
                  {user.caps.delete && (
                    <button
                      className="btn btn-sm btn-icon btn-danger media-item-delete"
                      onClick={() => setConfirmDelete(r)}
                      title={`Delete ${imageName(r)}`}
                      aria-label={`Delete ${imageName(r)}`}
                    >
                      <Icon name="trash" size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {query.data?.total ? (
              <div style={{ marginTop: 12 }}>
                <Pager
                  page={query.data.page}
                  pages={query.data.pages}
                  total={query.data.total}
                  limit={query.data.limit}
                  onPage={setPage}
                  onLimit={(n) => {
                    setLimit(n)
                    setPage(1)
                  }}
                  limits={PAGE_SIZES}
                  limitLabel="Per page"
                />
              </div>
            ) : null}
          </Card>
        ) : null}
      </div>

      <Modal open={uploadOpen} onClose={() => setUploadOpen(false)} title="Upload an image" size="lg">
        <ImageUpload
          kind="other"
          maxBytes={user.maxImageBytes}
          onUploaded={() => {
            query.refetch()
            setUploadOpen(false)
          }}
        />
      </Modal>

      <Modal
        open={!!selected}
        onClose={() => setSelected(null)}
        title={selected?.filename || 'Image'}
        size="lg"
        footer={
          <>
            {user.caps.update && (
              <button className="btn btn-primary" onClick={saveAlt} disabled={busy}>
                {busy ? <Spinner /> : 'Save alt text'}
              </button>
            )}
            <button className="btn" onClick={() => setSelected(null)}>
              Close
            </button>
            <span className="spacer" />
            {user.caps.delete && (
              <button className="btn btn-danger" onClick={() => setConfirmDelete(selected)}>
                Delete
              </button>
            )}
          </>
        }
      >
        {selected && (
          <div className="col">
            {/*
              Capped well below the viewport: the alt text field and the AI
              button underneath are the point of this dialog, and a tall image
              pushed them off the bottom.
            */}
            {/* Eager: a picture opened on purpose, in a dialog, and `.media-full` has no
                box of its own until it loads — see MediaImage. */}
            <MediaImage
              id={selected.id}
              alt={selected.alt_text}
              className="media-full"
              style={{ maxHeight: '36vh' }}
              eager
            />
            <Field
              label="Alt text"
              hint={
                [
                  selected.ai_caption
                    ? 'A vision model has written a description for this image.'
                    : user.caps.useAi
                      ? 'Write it yourself, or let a vision model draft it for you.'
                      : null,
                  /* Said because it happens without being typed, and because it comes
                     back after being deleted — text that reappears on save with no
                     explanation reads as the page losing an edit. */
                  selected.newsletter_id
                    ? 'This picture is on an update, so its S4CA code is kept at the front and the box below can be searched by it.'
                    : null,
                ]
                  .filter(Boolean)
                  .join(' ') || undefined
              }
            >
              <div className="row row-tight">
                <input
                  className="input"
                  style={{ flex: 1, width: 'auto', minWidth: 180 }}
                  value={alt}
                  onChange={(e) => setAlt(e.target.value)}
                  disabled={!user.caps.update}
                />
                {user.caps.useAi && user.caps.update && (
                  <AiAltTextButton mediaId={selected.id} onWritten={setAlt} size="" />
                )}
              </div>
            </Field>
            <dl className="kv">
              <dt>Stored size</dt>
              <dd>{fmtBytes(selected.bytes)}</dd>
              <dt>Original size</dt>
              <dd>{fmtBytes(selected.original_bytes)}</dd>
              <dt>Format</dt>
              <dd>{selected.mime_type}</dd>
              <dt>Dimensions</dt>
              <dd>{selected.width ? `${selected.width}${selected.height ? ` × ${selected.height}` : 'px wide'}` : '—'}</dd>
              <dt>Kind</dt>
              <dd>{selected.kind}</dd>
              <dt>Partner Unit</dt>
              <dd>
                {selected.partner_id ? (
                  <Link to={`/partners/${selected.partner_id}`}>{code(selected.s4ca_code)}</Link>
                ) : (
                  '—'
                )}
              </dd>
              <dt>Update</dt>
              <dd>
                {selected.newsletter_id ? (
                  <Link to={`/newsletters/${selected.newsletter_id}`}>{fmtDate(selected.newsletter_published_on)}</Link>
                ) : (
                  '—'
                )}
              </dd>
              <dt>Uploaded by</dt>
              <dd className="small">{selected.uploaded_by}</dd>
              <dt>Uploaded</dt>
              <dd className="small">{fmtDateTime(selected.created_at)}</dd>
              <dt>R2 key</dt>
              <dd className="mono tiny">{selected.r2_key}</dd>
            </dl>
          </div>
        )}
      </Modal>

      <ConfirmModal
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={remove}
        busy={busy}
        title="Delete this image?"
        body={
          confirmDelete && (
            <div className="col">
              <div className="row row-tight">
                <MediaImage
                  id={confirmDelete.id}
                  alt={confirmDelete.alt_text}
                  className="media-thumb"
                  style={{ width: 120, aspectRatio: '4 / 3', borderRadius: 'var(--r-sm)' }}
                />
                <div className="col" style={{ gap: 2, minWidth: 0 }}>
                  <strong className="truncate">{imageName(confirmDelete)}</strong>
                  <span className="small subtle">
                    {fmtBytes(confirmDelete.bytes)} · uploaded {fmtDateTime(confirmDelete.created_at)}
                  </span>
                </div>
              </div>
              {/* Say what else changes, so a picture that is doing a job somewhere is
                  never deleted on the assumption that it is a spare copy. */}
              {confirmDelete.partner_id ? (
                <p>
                  <strong>{code(confirmDelete.s4ca_code)}</strong> uses this image, and will fall back to the
                  placeholder photo.
                </p>
              ) : confirmDelete.newsletter_id ? (
                <p>
                  It is attached to{' '}
                  <Link to={`/newsletters/${confirmDelete.newsletter_id}`}>an update</Link>, and will
                  no longer appear there.
                </p>
              ) : null}
              <p>The object is removed from R2. This cannot be undone.</p>
            </div>
          )
        }
      />
    </PageHeader>
  )
}
